import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import { Close as CloseIcon, FileUpload as FileUploadIcon } from "@/components/icons";
import { staffManagementApi } from "../services/staff-management.api";
import { toast } from "sonner";
import styles from "./EditStaffModal.module.scss";

interface BulkImportStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ACCEPTED_TYPES = ".xlsx,.xls,.csv";

const BulkImportStaffModal: React.FC<BulkImportStaffModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setIsSubmitting(false);
    setIsDragging(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = (selectedFile: File | null) => {
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const ext = selectedFile.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext ?? "")) {
      toast.error(t("staffManagement.bulkImport.invalidFormat"));
      return;
    }
    setFile(selectedFile);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    handleFileSelect(f ?? null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    handleFileSelect(f ?? null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error(t("staffManagement.bulkImport.selectFile"));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await staffManagementApi.bulkImport(file);
      const imported = (res as { data?: { imported?: number } })?.data?.imported ?? 0;
      toast.success(
        t("staffManagement.bulkImport.success", {
          count: imported,
        }),
      );
      onSuccess?.();
      handleClose();
    } catch {
      // Error already shown by apiClient interceptor
    } finally {
      setIsSubmitting(false);
    }
  };

  const customHeader = (
    <div className={styles.modalHeader}>
      <div className={styles.modalHeaderLeft}>
        <FileUploadIcon width={24} height={24} color="#14181F" />
        <h3 className={styles.modalTitle}>
          {t("staffManagement.bulkImport.title")}
        </h3>
      </div>
      <button
        type="button"
        className={styles.closeButton}
        onClick={handleClose}
        aria-label={t("common.close")}
      >
        <CloseIcon width={20} height={20} color="#72808f" />
      </button>
    </div>
  );

  const content = (
    <div className={styles.formGrid}>
      <div className={styles.formFieldFull}>
        <p className={styles.bulkImportHint}>
          {t("staffManagement.bulkImport.hint")}
        </p>
        <div
          className={`${styles.bulkImportDropzone} ${isDragging ? styles.dragging : ""} ${file ? styles.hasFile : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleInputChange}
            className={styles.bulkImportInput}
          />
          {file ? (
            <span className={styles.bulkImportFileName}>{file.name}</span>
          ) : (
            <span className={styles.bulkImportPlaceholder}>
              {t("staffManagement.bulkImport.dropPlaceholder")}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const customFooter = (
    <div className={styles.footerButtons}>
      <Button variant="text" color="secondary" type="button" onClick={handleClose}>
        {t("staffManagement.bulkImport.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        type="button"
        onClick={handleSubmit}
        disabled={!file || isSubmitting}
      >
        {isSubmitting
          ? t("common.loading")
          : t("staffManagement.bulkImport.submit")}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("staffManagement.bulkImport.title")}
      customHeader={customHeader}
      customFooter={customFooter}
      size="md"
    >
      {content}
    </Modal>
  );
};

export default BulkImportStaffModal;
